import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import AppWrapper from './components/layout/AppWrapper';
import PublicLayoutSwitch from './components/layout/PublicLayoutSwitch';
import PanelLayout from './components/layout/PanelLayout';
import ErrorBoundary from './components/layout/ErrorBoundary';
import ToastContainer from './components/common/ToastContainer';
import StorefrontRouter from './components/layout/StorefrontRouter';
import RootRoute from './components/layout/RootRoute';
import CatalogRoute from './components/layout/CatalogRoute';
import NotFoundPage from './pages/errors/NotFoundPage';

// Pages - Reviter (principal). ReviterHomePage é renderizada via RootRoute
// (que decide LP vs storefront pelo tenant).
import ProductDetailPage from './pages/product/ProductDetailPage';
import CartPage from './pages/cart/CartPage';
import CheckoutPage from './pages/checkout/CheckoutPage';
import OrderConfirmation from './pages/checkout/OrderConfirmation';
import BecomeResellerPage from './pages/reseller/BecomeResellerPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Pages - Reseller
import ResellerDashboard from './pages/reseller/ResellerDashboard';
import ResellerOrders from './pages/reseller/ResellerOrders';
import ResellerOrderDetail from './pages/reseller/ResellerOrderDetail';
import ResellerProfile from './pages/reseller/ResellerProfile';
import ResellerPayments from './pages/reseller/ResellerPayments';
import ResellerPaymentDetail from './pages/reseller/ResellerPaymentDetail';
import ResellerCatalog from './pages/reseller/ResellerCatalog';

// Pages - Supplier (dono de loja)
import SupplierDashboard from './pages/supplier/SupplierDashboard';
import ProductsManagement from './pages/supplier/ProductsManagement';
import CategoriesManagement from './pages/supplier/CategoriesManagement';
import CampaignsManagement from './pages/supplier/CampaignsManagement';
import ProductForm from './pages/supplier/ProductForm';
import OrdersManagement from './pages/supplier/OrdersManagement';
import OrderDetail from './pages/supplier/OrderDetail';
import ThemeConfigPage from './pages/supplier/ThemeConfigPage';
import LeadsManagement from './pages/supplier/LeadsManagement';
import ResellersManagement from './pages/supplier/ResellersManagement';
import PaymentsManagement from './pages/supplier/PaymentsManagement';
import StoreUsersManagement from './pages/supplier/StoreUsersManagement';

// Pages - Client (/minha-conta)
import ClientDashboard from './pages/client/ClientDashboard';
import ClientOrders from './pages/client/ClientOrders';
import ClientAddresses from './pages/client/ClientAddresses';

// Pages - Platform admin (/admin-reviter)
import AdminGate from './pages/admin/AdminGate';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminStores from './pages/admin/AdminStores';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCreateStore from './pages/admin/AdminCreateStore';
import AdminStoreDetail from './pages/admin/AdminStoreDetail';
import AdminUserDetail from './pages/admin/AdminUserDetail';

// Dev-only — preview de primitivos do DS em /dev/componentes
import ComponentsPreview from './pages/dev/ComponentsPreview';
// Dev-only — debug de tema aplicado + troca entre presets das lojas de seed
import ThemeDebugPage from './pages/dev/ThemeDebugPage';

// Rotas de painel precisam gate de admin pra /admin-reviter.
// Wrappa pra compor com o PanelLayout.
const AdminRoute = ({ children }) => <AdminGate>{children}</AdminGate>;

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <NotificationsProvider>
        <CartProvider>
          <ToastProvider>
            <Router>
              <AppWrapper>
                <ToastContainer />
                <Routes>
                {/* Rotas públicas — PublicLayoutSwitch escolhe entre Reviter
                    (institucional) e StorefrontLayout (whitelabel) por tenant. */}
                <Route element={<PublicLayoutSwitch />}>
                  <Route path="/" element={<RootRoute />} />
                  <Route path="/catalogo" element={<CatalogRoute />} />
                  <Route path="/produto/:id" element={<ProductDetailPage />} />
                  <Route path="/carrinho" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/pedido/:id/confirmacao" element={<OrderConfirmation />} />
                  <Route path="/quero-ser-revendedor" element={<BecomeResellerPage />} />
                  <Route path="/entrar" element={<LoginPage />} />
                  <Route path="/cadastrar" element={<SignupPage />} />
                  <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
                  <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
                  {/* Logins dedicados de backoffice — Doc Seção 10. Sem branding da store. */}
                  <Route path="/admin-reviter/login" element={<LoginPage mode="backoffice-reviter" />} />
                  <Route path="/fornecedor/painel/login" element={<LoginPage mode="backoffice-store" />} />
                </Route>

                {/* Painéis logados (admin/supplier/reseller/client) */}
                <Route element={<PanelLayout />}>
                  {/* Painel Revendedor */}
                  <Route path="/revendedor/painel" element={<ResellerDashboard />} />
                  <Route path="/revendedor/painel/catalogo" element={<ResellerCatalog />} />
                  <Route path="/revendedor/painel/pedidos" element={<ResellerOrders />} />
                  <Route path="/revendedor/painel/pedidos/:id" element={<ResellerOrderDetail />} />
                  <Route path="/revendedor/painel/perfil" element={<ResellerProfile />} />
                  <Route path="/revendedor/painel/pagamentos" element={<ResellerPayments />} />
                  <Route path="/revendedor/painel/pagamentos/:id" element={<ResellerPaymentDetail />} />

                  {/* Painel Dono de Loja */}
                  <Route path="/fornecedor/painel" element={<SupplierDashboard />} />
                  <Route path="/fornecedor/painel/produtos" element={<ProductsManagement />} />
                  <Route path="/fornecedor/painel/categorias" element={<CategoriesManagement />} />
                  <Route path="/fornecedor/painel/campanhas" element={<CampaignsManagement />} />
                  <Route path="/fornecedor/painel/produtos/novo" element={<ProductForm />} />
                  <Route path="/fornecedor/painel/produtos/:id/editar" element={<ProductForm />} />
                  <Route path="/fornecedor/painel/pedidos" element={<OrdersManagement />} />
                  <Route path="/fornecedor/painel/pedidos/:id" element={<OrderDetail />} />
                  <Route path="/fornecedor/painel/configuracoes" element={<ThemeConfigPage />} />
                  <Route path="/fornecedor/painel/leads" element={<LeadsManagement />} />
                  <Route path="/fornecedor/painel/revendedores" element={<ResellersManagement />} />
                  <Route path="/fornecedor/painel/pagamentos" element={<PaymentsManagement />} />
                  <Route path="/fornecedor/painel/usuarios" element={<StoreUsersManagement />} />

                  {/* Painel Cliente */}
                  <Route path="/minha-conta" element={<ClientDashboard />} />
                  <Route path="/minha-conta/pedidos" element={<ClientOrders />} />
                  <Route path="/minha-conta/pedidos/:id" element={<ResellerOrderDetail />} />
                  <Route path="/minha-conta/enderecos" element={<ClientAddresses />} />

                  {/* Painel Platform Admin — AdminGate envolve cada página */}
                  <Route path="/admin-reviter" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
                  <Route path="/admin-reviter/lojas" element={<AdminRoute><AdminStores /></AdminRoute>} />
                  <Route path="/admin-reviter/lojas/nova" element={<AdminRoute><AdminCreateStore /></AdminRoute>} />
                  <Route path="/admin-reviter/lojas/:id" element={<AdminRoute><AdminStoreDetail /></AdminRoute>} />
                  <Route path="/admin-reviter/usuarios" element={<AdminRoute><AdminUsers /></AdminRoute>} />
                  <Route path="/admin-reviter/usuarios/:id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
                </Route>

                {/* Dev-only preview — sem layout */}
                <Route path="/dev/componentes" element={<ComponentsPreview />} />
                <Route path="/dev/tema" element={<ThemeDebugPage />} />

                  {/* Whitelabel do fornecedor — StorefrontRouter decide sub-rotas
                      baseado em store.businessType (catalog oculta /carrinho e /checkout). */}
                  <Route path="/fornecedor/:supplierSlug/*" element={<StorefrontRouter />} />

                  {/* 404 catch-all */}
                  <Route element={<PublicLayoutSwitch />}>
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Routes>
              </AppWrapper>
            </Router>
          </ToastProvider>
        </CartProvider>
        </NotificationsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
